import { useState } from "react";
import { toast } from "sonner";
import { FileDown, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormField } from "@/components/FormBits";
import { QuoteItems, newQuoteItem } from "@/components/QuoteItems";
import { QuoteImages } from "@/components/QuoteImages";
import { api, formatError } from "@/lib/api";

const INITIAL = { customer_name: "", customer_phone: "", customer_email: "", customer_type: "Cliente", payment_method: "", payment: "", status: "Em aprovação", delivery: "", notes: "", art_files: "" };

const Card = ({ title, children }) => (
  <section className="space-y-4 rounded-md border border-slate-200 bg-white p-5">
    <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">{title}</h2>
    {children}
  </section>
);

const Area = ({ id, label, value, onChange, placeholder }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Textarea id={id} rows={3} value={value} onChange={onChange} placeholder={placeholder} data-testid={`input-${id}`} />
  </div>
);

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const blobError = async (e) => {
  try {
    const d = JSON.parse(await e.response.data.text()).detail;
    return typeof d === "string" ? d : "Confira os campos do orçamento.";
  } catch {
    return formatError(e);
  }
};

export default function AdminQuote() {
  const [f, setF] = useState(INITIAL);
  const [items, setItems] = useState([newQuoteItem()]);
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const reset = () => { setF(INITIAL); setItems([newQuoteItem()]); setImages([]); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, items: items.map(({ key, ...it }) => ({ ...it, quantity: Number(it.quantity), unit_price: it.unit_price === "" ? null : Number(it.unit_price), total: Number(it.total) })), images: images.map((i) => i.src) };
      const { data } = await api.post("/admin/quotes/pdf", payload, { responseType: "blob" });
      const slug = f.customer_name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w]+/g, "-").toLowerCase().replace(/^-|-$/g, "");
      saveBlob(data, `orcamento-${slug || "cliente"}.pdf`);
      toast.success("PDF do orçamento gerado");
    } catch (err) {
      toast.error(await blobError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-4xl space-y-6" data-testid="quote-form">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="quotes-title">Orçamentos</h1>
        <p className="text-slate-500">Preencha os dados e gere o PDF do orçamento. Nada fica salvo no painel.</p>
      </div>
      <Card title="Dados do cliente">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id="quote-customer-name" label="Nome" required maxLength={120} value={f.customer_name} onChange={set("customer_name")} />
          <FormField id="quote-customer-phone" label="Telefone" maxLength={40} value={f.customer_phone} onChange={set("customer_phone")} />
          <FormField id="quote-customer-email" label="E-mail" type="email" maxLength={120} value={f.customer_email} onChange={set("customer_email")} />
          <div className="space-y-1.5">
            <Label htmlFor="quote-customer-type">Tipo</Label>
            <select id="quote-customer-type" value={f.customer_type} onChange={set("customer_type")} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="select-quote-customer-type">
              {["Cliente", "Revendedor", "Loja"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
      </Card>
      <Card title="Itens do pedido"><QuoteItems items={items} onChange={setItems} /></Card>
      <Card title="Pagamento e status">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id="quote-payment-method" label="Forma de pagamento" placeholder="Ex.: Pix, cartão ou dinheiro" value={f.payment_method} onChange={set("payment_method")} />
          <FormField id="quote-payment" label="Pagamento" placeholder="Ex.: 50% na aprovação" value={f.payment} onChange={set("payment")} />
          <FormField id="quote-status" label="Status do pedido" value={f.status} onChange={set("status")} />
          <FormField id="quote-delivery" label="Entrega" placeholder="Ex.: Retirada na loja" value={f.delivery} onChange={set("delivery")} />
        </div>
      </Card>
      <Card title="Observações e arte">
        <Area id="quote-notes" label="Observações do pedido" value={f.notes} onChange={set("notes")} placeholder="Ex.: Tempo de produção: 2 dias" />
        <Area id="quote-art-files" label="Arquivos de arte" value={f.art_files} onChange={set("art_files")} placeholder="Ex.: Desenvolvido pela gráfica, podendo ter reajuste pelo cliente." />
      </Card>
      <Card title="Imagens (fim do PDF)"><QuoteImages images={images} onChange={setImages} /></Card>
      <div className="flex flex-wrap justify-end gap-3">
        <Button type="button" variant="outline" onClick={reset} className="rounded-full" data-testid="btn-reset-quote"><RotateCcw className="h-4 w-4" /> Limpar</Button>
        <Button type="submit" disabled={busy} className="rounded-full bg-[#0099DD] px-6 hover:bg-sky-700" data-testid="btn-generate-quote-pdf"><FileDown className="h-4 w-4" /> {busy ? "Gerando..." : "Gerar PDF"}</Button>
      </div>
    </form>
  );
}
